import {
  Bolt,
  Close,
  FilterList,
  MyLocation,
  Place,
} from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import _, { debounce } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Geocode from "react-geocode";
import MapContext from "../../Contexts/MapContext";
import UserContext from "../../Contexts/UserContext";
import { sortOptions } from "../../Utils/SearchUtils";
import {
  calculateMaxMi,
  getInitialCoordinates,
  sortByCoordinates,
  startCase,
} from "../../Utils/Utils";
import { useSearchLogic } from "../../Utils/searchHook";
import PlacesAutocomplete, {
  geocodeBySuggestion,
} from "../Components/PlacesAutocomplete";
import ResultList from "./ResultList";
import BuyOnlineButton from "./BuyOnlineButton";

Geocode.setApiKey(process.env.REACT_APP_GOOGLE_API_KEY);

const TOP_RETAILERS = [
  "Walmart",
  "Target",
  "Whole Foods Market",
  "Kroger",
  "Morton Williams Supermarket",
  "ShopRite",
];

export default function PDP({
  addLayer,
  onFilterChange,
  fetchDelivery,
  region,
  setShowSearch,
}) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const theme = useTheme();

  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const { results = [] } = mapContext;

  const renderCount = useRef(0);
  renderCount.current = renderCount.current + 1;

  const [selectedSort, setSelectedSort] = useState(
    userContext.state?.config?.defaultSort,
  );
  const [mapIsMoving, setIsMoving] = useState(false);
  const [term, setTerm] = useState(params.get("term") || "");
  const [suggestSuccess, setSuggestSuccess] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState(
    params.get("filters")
      ? JSON.parse(params.get("filters"))
      : { chain: "", type: "", product: [] },
  );
  const [isSuggestingStore, setIsSuggestingStore] = useState(false);
  const [listUpdates, setListUpdates] = useState(1);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const isStrictProductSearch = useRef(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const setResults = useCallback(
    (results) => {
      mapContext.setMapContext((oldState) => {
        const newResults =
          typeof results === "function" ? results(oldState.results) : results;
        return {
          ...oldState,
          results: newResults,
        };
      });
    },
    // mapContext.setMapContext is a stable useState setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapContext.setMapContext],
  );

  const {
    loading,
    searchParams,
    setSearchParams,
    search,
    searchWithTerm,
  } = useSearchLogic({
    userContext,
    mapContext,
    filters,
    isStrictProductSearch,
    addLayer,
    fetchDelivery,
    setResults,
    setShowSearch,
    setIsSuggestingStore,
    params,
  });

  const searchRef = useRef(search);
  searchRef.current = search;
  const searchWithTermRef = useRef(searchWithTerm);
  searchWithTermRef.current = searchWithTerm;

  const storeLabel =
    userContext.state?.orgProperties?.storeNameReplacement || "Store";
  const productLabel =
    userContext.state?.orgProperties?.productNameReplacement || "Product";

  const handleKeyDown = (event) => {
    setTerm(event.target.value || "");

    if (event.key === "Enter") {
      setSearchOpen(false);
      searchWithTerm(undefined, term);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (pos?.coords) {
          setTerm("");
          setSearchParams((oldParams) => ({
            coordinates: [pos.coords.latitude, pos.coords.longitude],
            zoom: oldParams.zoom,
            _searchParams: {
              ...oldParams?._searchParams,
              searchedFirstTime:
                oldParams?._searchParams?.searchedFirstTime === undefined
                  ? false
                  : true,
            },
          }));
        }
      },
      (err) => {
        console.error("PDP.js:getCurrentPosition", err);
      },
    );
  };

  const onSuggestionSelected = (suggestion) => {
    setTerm(suggestion.description);
    geocodeBySuggestion(suggestion)
      .then((results) => {
        if (results.length < 1) {
          console.error("Couldn't find the location you entered");
          return;
        }

        const zipcodeIndex = _.findKey(results[0].address_components, {
          types: ["postal_code"],
        });
        const zipcode =
          zipcodeIndex > -1
            ? results[0].address_components[zipcodeIndex].long_name
            : "";

        const { geometry } = results[0];
        let geopoliticalRegion = "street_address";
        for (const type of [
          "street_address",
          "postal_code",
          "locality",
          "administrative_area_level_1",
          "country",
        ]) {
          if (suggestion.types.includes(type)) {
            geopoliticalRegion = type;
            break;
          }
        }

        setSearchParams((oldParams) => ({
          coordinates: [geometry.location.lat(), geometry.location.lng()],
          zoom: oldParams.zoom,
          _searchParams: {
            ...oldParams?._searchParams,
            searchedFirstTime:
              oldParams?._searchParams?.searchedFirstTime === undefined
                ? false
                : true,
            zipcode,
            geopoliticalRegion,
            term: suggestion.description,
          },
        }));
      })
      .catch((err) => {
        console.error("PDP.js:geocodeBySuggestion", err);
      });
  };

  const handleClose = () => {
    window.parent.postMessage({ action: "close" }, "*");
  };

  const handleViewMap = () => {
    window.parent.postMessage({ action: "view-map" }, "*");
  };

  const doReset = () => {
    setResults([]);
    mapContext.toggleModalPresent?.();
    setTerm("");
  };

  const showResultDetailsPage = (
    res,
    event,
    reviews,
    index,
    openInfo,
    filters,
  ) => {
    mapContext.toggleModalPresent?.(res, reviews, index, openInfo, filters);
  };

  const debouncedAddLayer = useMemo(
    () =>
      debounce((addLayer, mapContext, selectedSort, setResults) => {
        setResults((oldResults) => {
          const newResults = selectedSort
            ? sortOptions[selectedSort](oldResults)
            : sortByCoordinates(
                oldResults,
                mapContext.viewport.latitude,
                mapContext.viewport.longitude,
              );
          addLayer(newResults, false);
          return newResults;
        });
        setListUpdates((oldUpdate) => oldUpdate + 1);
      }, 500),
    [],
  );

  useEffect(() => {
    const updateMapViewWithCoordinates = async (pos) => {
      if (pos.coords) {
        setSearchParams((oldParams) => ({
          ...oldParams,
          coordinates: [pos.coords.latitude, pos.coords.longitude],
          zoom: mapContext.viewport.zoom,
          _searchParams: {
            ...oldParams?._searchParams,
            searchedFirstTime:
              oldParams?._searchParams?.searchedFirstTime === undefined
                ? false
                : true,
            preventFly: true,
          },
        }));
      }
    };

    if (
      searchParams.coordinates &&
      window.turf?.point &&
      window.turf?.distance &&
      !isNaN(searchParams.coordinates[0]) &&
      !isNaN(searchParams.coordinates[1]) &&
      !isNaN(mapContext.viewport.latitude) &&
      !isNaN(mapContext.viewport.longitude)
    ) {
      const from = window.turf.point(searchParams.coordinates);
      const to = window.turf.point([
        mapContext.viewport.latitude,
        mapContext.viewport.longitude,
      ]);
      const distance = window.turf.distance(from, to, { units: "miles" });
      const searchDistance = calculateMaxMi(
        searchParams.coordinates[0],
        searchParams.zoom,
      );

      const isZoomingOut =
        mapContext.isZooming &&
        searchParams.zoom &&
        searchParams.zoom - mapContext.viewport.zoom >= 1;
      const movingPastSearchDistance =
        distance > searchDistance && (term === "" || mapContext.isDragging);
      if (
        !mapContext.inTransition &&
        (isZoomingOut || movingPastSearchDistance)
      ) {
        updateMapViewWithCoordinates({
          coords: {
            latitude: mapContext.viewport.latitude,
            longitude: mapContext.viewport.longitude,
          },
        });
      }
    } else {
      updateMapViewWithCoordinates({
        coords: {
          latitude: mapContext.viewport.latitude,
          longitude: mapContext.viewport.longitude,
        },
      });
    }

    if (
      !mapContext.inTransition &&
      !mapContext.isDragging &&
      !mapContext.isZooming
    ) {
      debouncedAddLayer(addLayer, mapContext, selectedSort, setResults);
    }
  }, [
    selectedSort,
    searchParams,
    mapContext.inTransition,
    mapContext.isDragging,
    mapContext.isZooming,
    mapContext.viewport.latitude,
    mapContext.viewport.longitude,
    mapContext.viewport.zoom,
    term,
    addLayer,
    setResults,
    debouncedAddLayer,
    setSearchParams,
  ]);

  useEffect(() => {
    if (renderCount.current === 7) {
      const initialCoordinates = getInitialCoordinates(
        userContext.state?.config,
        userContext.state?.orgProperties,
        searchParams.zoom,
        region,
      );
      mapContext.updateViewport?.({
        ...mapContext.viewport,
        latitude: initialCoordinates.lat,
        longitude: initialCoordinates.lng,
        zoom: searchParams.zoom,
      });
    }
  }, [mapContext, userContext.state, searchParams.zoom, region]);

  useEffect(() => {
    const { coordinates, zoom, _searchParams } = searchParams;
    if (coordinates?.length === 2 && zoom) {
      if (params.get("zipcode")) {
        searchWithTermRef.current(params.get("zipcode"));
        const url = new URL(window.location);
        url.searchParams.delete("coordinates");
        url.searchParams.delete("zipcode");
        window.history.pushState({}, "", "?" + url.searchParams.toString());
        return;
      }
      searchRef.current(
        { lat: coordinates[0], lng: coordinates[1] },
        zoom,
        _searchParams,
      );
    }
    // search/searchWithTerm are called via refs to avoid re-running on
    // their identity changes (which would cause an infinite search loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, params]);

  useEffect(() => {
    if (!mapContext.deliveryInventory) {
      setTabIndex(0);
    }
  }, [mapContext.deliveryInventory]);

  if (mapContext.trackUserLocation && term !== "") {
    setTerm("");
  }

  function IconFilter({ label, onToggle, isSelected, icon }) {
    return (
      <Stack
        direction="column"
        spacing={0}
        alignItems="center"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <div
          style={{
            borderRadius: 200,
            padding: 3,
            border: `2px solid ${
              isSelected ? theme.palette.primary.main : "transparent"
            }`,
            position: "relative",
          }}
        >
          {isSelected && (
            <IconButton
              aria-label="remove type filter"
              style={{
                borderRadius: 200,
                position: "absolute",
                top: -5,
                right: -5,
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.contrastText,
                padding: 1,
              }}
              size="small"
              onClick={onToggle}
            >
              <Close fontSize="inherit" />
            </IconButton>
          )}
          <div
            style={{
              borderRadius: 200,
              padding: 3,
              height: "2em",
              width: "2em",
            }}
          >
            {icon}
          </div>
        </div>
        {label && (
          <Typography color="#595959" style={{ fontSize: 10 }}>
            {startCase(label)}
          </Typography>
        )}
      </Stack>
    );
  }

  const getTopRetailers = () => {
    const retailersByCount = (results || []).reduce((acc, item) => {
      const retailer = item.chain;
      if (!retailer) {
        return acc;
      }
      if (!acc[retailer]) {
        acc[retailer] = 0;
      }
      acc[retailer]++;
      return acc;
    }, {});
    return Object.keys(retailersByCount)
      .sort((a, b) => retailersByCount[b] - retailersByCount[a])
      .filter((retailer) => TOP_RETAILERS.includes(retailer));
  };

  const getRetailLogo = (retailer) => {
    return (
      <img
        src={`https://dathic-growth-files-public2.s3.us-east-1.amazonaws.com/store-locator-online-option-images/${retailer}Logo.png`}
        alt={retailer}
        style={{ width: "100%", height: "100%" }}
      />
    );
  };

  const setChainFilter = (value) => {
    setFilters({ ...filters, chain: value });
    onFilterChange?.({ ...filters, chain: value });
  };

  const sectionLabelSx = {
    color: "#9a9a9a",
    fontWeight: 600,
    letterSpacing: 1,
  };

  const segmentBtnSx = (active) => ({
    borderRadius: 999,
    textTransform: "none",
    px: 3,
    boxShadow: "none",
    fontWeight: 600,
    color: active ? undefined : "#595959",
    backgroundColor: active ? theme.palette.primary.main : "transparent",
    "&:hover": {
      backgroundColor: active ? theme.palette.primary.main : "rgba(0,0,0,0.04)",
      boxShadow: "none",
    },
  });

  const searchAndControls = () => (
    <form id="search-panel" onSubmit={(e) => e.preventDefault()}>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flex: 1,
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 999,
              px: 2,
              py: 0.25,
            }}
          >
            <div style={{ flex: 1 }}>
              <PlacesAutocomplete
                open={searchOpen}
                onOpen={() => setSearchOpen(true)}
                onClose={() => setSearchOpen(false)}
                textFieldProps={{
                  onKeyDown: (event) => handleKeyDown(event),
                  onChange: (e) => {
                    setTerm(e.target.value || "");
                  },
                  onFocus: () => {
                    setShowSearch(true);
                  },
                  fullWidth: true,
                  value: term,
                  label: "",
                  placeholder: "Enter zip code or city...",
                  type: "search",
                  name: "address",
                  variant: "standard",
                  InputProps: { disableUnderline: true },
                }}
                onSuggestionSelected={(e) => {
                  if (e) {
                    onSuggestionSelected(e);
                  } else {
                    doReset();
                  }
                }}
                createAutocompleteRequest={(inputValue) => {
                  const isSearchingAddress = /\d+.*[A-z]/.exec(inputValue);
                  return {
                    input: inputValue,
                    types: [isSearchingAddress ? "geocode" : "(regions)"],
                  };
                }}
              />
            </div>
            <IconButton
              aria-label="use my location"
              onClick={handleUseMyLocation}
              sx={{ color: theme.palette.primary.main }}
            >
              <MyLocation />
            </IconButton>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<Place />}
            onClick={handleViewMap}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              color: "#222",
              borderColor: "#e0e0e0",
              backgroundColor: "#fff",
              px: 2.5,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            View Map
          </Button>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          {!!userContext.state?.config?.showBuyOnlineButton && (
            <Stack
              direction="row"
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 999,
                p: "4px",
              }}
            >
              <Button
                disableElevation
                variant={tabIndex === 0 ? "contained" : "text"}
                onClick={() => setTabIndex(0)}
                sx={segmentBtnSx(tabIndex === 0)}
              >
                In Store
              </Button>
              <Button
                disableElevation
                variant={tabIndex === 1 ? "contained" : "text"}
                onClick={() => setTabIndex(1)}
                disabled={!mapContext.deliveryInventory}
                id="map-buy-online"
                sx={{ ...segmentBtnSx(tabIndex === 1), position: "relative" }}
              >
                Online
                {!mapContext.deliveryInventory && (
                  <CircularProgress
                    style={{ position: "absolute", right: 4, top: 8 }}
                    color="inherit"
                    size={16}
                  />
                )}
              </Button>
            </Stack>
          )}
          {tabIndex === 0 && !!getTopRetailers().length && (
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                color: "#222",
                borderColor: "#e0e0e0",
                backgroundColor: "#fff",
              }}
            >
              Filter Retailers
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  const searchPanelStack = () => {
    return (
      <Stack
        direction="column"
        spacing={1}
        sx={{ flex: 1, minHeight: 0, pt: 1 }}
      >
        {!isSuggestingStore && searchAndControls()}

        <Popover
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              padding: 16,
              maxWidth: 280,
            }}
          >
            {getTopRetailers().map((retailer) => (
              <IconFilter
                key={retailer}
                label={retailer}
                icon={getRetailLogo(retailer.replaceAll(" ", ""))}
                onToggle={() =>
                  setChainFilter(filters.chain === retailer ? "" : retailer)
                }
                isSelected={(filters.chain || "").includes(retailer)}
              />
            ))}
          </div>
        </Popover>

        {!isSuggestingStore && tabIndex === 0 && (
          <Stack direction="column" spacing={1} sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="overline" sx={sectionLabelSx}>
              Nearby {storeLabel}s
            </Typography>
            {loading && (
              <Stack>
                <Typography sx={{ color: "#595959" }}>
                  Searching for {storeLabel}...
                </Typography>
                <LinearProgress />
              </Stack>
            )}
            {!results?.length && !loading && (
              <Stack alignItems="center" style={{ textAlign: "center" }}>
                <Typography>
                  No {storeLabel} in this area for your search
                </Typography>
              </Stack>
            )}
            {!!results?.length && !mapIsMoving && (
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResultList
                  key={listUpdates}
                  filters={filters}
                  data={
                    selectedSort ? sortOptions[selectedSort](results) : results
                  }
                  term={term}
                  showResultDetailsPage={showResultDetailsPage}
                />
              </div>
            )}
          </Stack>
        )}
        {!isSuggestingStore &&
          tabIndex === 1 &&
          userContext.state?.config?.showBuyOnlineButton && (
            <Stack
              direction="column"
              spacing={1}
              sx={{ flex: 1, minHeight: 0 }}
            >
              <Typography variant="overline" sx={sectionLabelSx}>
                Online Retailers
              </Typography>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <BuyOnlineButton products={mapContext.products} />
              </div>
            </Stack>
          )}
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      style={{
        margin: "0px",
        padding: 0,
        background:
          userContext.state?.productDetailPage?.background_color || "#eee",
        width: "100%",
        height: "100vh",
        position: "absolute",
        left: 0,
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          padding: isMobile ? "20px 20px 12px" : "28px 32px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          Where to Buy
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "#595959" }}
        >
          <Close />
        </IconButton>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "0 20px" : "0 32px",
        }}
      >
        {searchPanelStack()}
      </div>
      <div
        style={{
          borderTop: "1px solid #e6e6e6",
          padding: isMobile ? "10px 20px" : "12px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!userContext.state?.config?.hideAvailabilityMessage ? (
          <Typography
            variant="caption"
            style={{ fontStyle: "italic", color: "#7a7a7a" }}
          >
            Always check availability with store
          </Typography>
        ) : (
          <span />
        )}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Bolt
            fontSize="small"
            style={{ color: theme.palette.primary.main }}
          />
          {userContext.state?.config?.disableAttributionLink ? (
            <span
              id="dathic-powered"
              style={{
                fontSize: 12,
                fontStyle: "italic",
                color: "#444",
              }}
            >
              by Dathic
            </span>
          ) : (
            <a
              id="dathic-powered"
              href="https://dathic.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                fontStyle: "italic",
                color: "#444",
                textDecoration: "none",
              }}
            >
              by Dathic
            </a>
          )}
        </Stack>
      </div>
    </Paper>
  );
}
